import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CreditCard, Loader2, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '@/context/LanguageContext';
import { trackInitiateCheckout } from '@/utils/metaPixel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Payment = () => {
  const { t } = useLanguage();
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [application, setApplication] = useState(null);
  const [fetchingApp, setFetchingApp] = useState(true);
  const [checkoutTracked, setCheckoutTracked] = useState(false);

  useEffect(() => {
    fetchApplication();
  }, [applicationId]);

  // Track InitiateCheckout when application is loaded
  useEffect(() => {
    if (application && !checkoutTracked) {
      trackInitiateCheckout({
        applicationId: applicationId,
        value: application.total_visa_fee || 500,
        visaType: application.visa_duration === '30_days' ? '30 Days Visa' : '60 Days Visa',
        nationality: application.nationality
      });
      setCheckoutTracked(true);
    }
  }, [application, checkoutTracked, applicationId]);

  const fetchApplication = async () => {
    try {
      // For payment page, we need to fetch without strict email verification
      // since user just submitted and has the valid application ID
      // The application ID itself serves as proof of ownership here
      const response = await axios.get(`${API}/applications/${applicationId}?email=payment_flow`);
      setApplication(response.data);
      
      if (response.data.payment_status === 'paid') {
        toast.info(t.track.paid);
        navigate('/track');
      }
    } catch (error) {
      console.error('Error fetching application:', error);
      // If 403, try alternative approach - redirect to track page
      if (error.response?.status === 403 || error.response?.status === 401) {
        toast.error('Please track your application using your email');
        navigate('/track');
      } else {
        toast.error(t.common.error);
        navigate('/');
      }
    } finally {
      setFetchingApp(false);
    }
  };

  const handlePayment = async () => {
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/payment/create-intent`, {
        application_id: applicationId,
        amount_aed: application.total_visa_fee || 500
      });

      if (response.data.redirect_url) {
        window.location.href = response.data.redirect_url;
      } else {
        toast.error(t.common.error);
      }
    } catch (error) {
      console.error('Payment error:', error);
      const errorMsg = error.response?.data?.detail || t.common.error;
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (fetchingApp) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1A4D7F]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-12" data-testid="payment-page">
      <div className="container mx-auto px-6 max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 text-center"
        >
          <div className="w-16 h-16 bg-[#009FB7] rounded-full flex items-center justify-center mx-auto mb-4">
            <CreditCard className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-heading font-bold text-[#1A4D7F] mb-3">
            {t.payment.title}
          </h1>
          <p className="text-slate-600">{t.payment.subtitle}</p>
        </motion.div>

        <Card className="border-slate-200 shadow-lg">
          <CardHeader className="bg-white border-b border-slate-100">
            <CardTitle className="text-xl font-heading">{t.payment.summary}</CardTitle>
            <CardDescription>{t.payment.reviewOrder}</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            {application && (
              <div className="space-y-6">
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-600">{t.payment.applicantName}:</span>
                    <span className="font-medium">{application.first_name} {application.last_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">{t.payment.nationality}:</span>
                    <span className="font-medium">{application.nationality || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">{t.payment.totalApplicants}:</span>
                    <span className="font-medium">{application.total_applicants || 1}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">{t.payment.applicationId}:</span>
                    <span className="font-medium font-mono text-sm">{application.id.substring(0, 12)}...</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">{t.payment.service}:</span>
                    <span className="font-medium">{t.payment.visaProcessing}</span>
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-slate-600">{t.payment.feePerPerson}:</span>
                    <span className="font-medium">{application.visa_fee_per_person || 500} AED</span>
                  </div>
                  <div className="flex justify-between items-center mb-6">
                    <span className="text-xl font-heading font-bold text-[#1A4D7F]">{t.payment.totalAmount}:</span>
                    <span className="text-3xl font-heading font-bold text-[#1A4D7F]">{application.total_visa_fee || 500} AED</span>
                  </div>

                  <Button
                    onClick={handlePayment}
                    disabled={loading}
                    className="w-full bg-[#009FB7] hover:bg-[#008fa5] h-14 text-lg font-semibold"
                    data-testid="proceed-to-payment-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        {t.payment.processing}
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5 mr-2" />
                        {t.payment.payNow} - {application.total_visa_fee || 500} AED
                      </>
                    )}
                  </Button>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-blue-900 font-medium">{t.payment.securePayment}</p>
                      <p className="text-sm text-blue-700">
                        {t.payment.securePaymentDesc}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="text-center text-sm text-slate-500">
                  <p>{t.payment.needHelp} <a href="mailto:customer-support@aonevisas.com" className="text-[#1A4D7F] hover:underline">customer-support@aonevisas.com</a></p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Payment;
